import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import Section from '../components/Section'
import Button from '../components/Button'
import Card from '../components/Card'
import WLogo from '../components/WLogo'

export default function Login() {
    const navigate = useNavigate()
    const { login } = useAuth()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setIsLoading(true)

        try {
            const success = await login(formData.email, formData.password)
            if (success) {
                navigate('/app')
            } else {
                setError('Invalid email or password')
            }
        } catch {
            setError('Something went wrong. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    return (
        <Section className="py-12 lg:py-20">
            <div className="max-w-md mx-auto">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2 mb-8">
                        <WLogo className="w-8 h-8" />
                        <span className="text-xl font-semibold text-primary-color">Way2Will</span>
                    </Link>
                    <h1 className="text-3xl font-bold text-primary-color mb-4">
                        Welcome Back
                    </h1>
                    <p className="text-lg text-secondary-color">
                        Log in to continue to your dashboard
                    </p>
                </div>

                <Card variant="elevated" className="p-8">
                    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                        {error && (
                            <div className="p-3 rounded-lg bg-error/10 text-error text-sm">
                                {error}
                            </div>
                        )}

                        <div className="flex flex-col gap-2">
                            <label htmlFor="email" className="text-sm font-medium text-primary-color">
                                Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="your@email.com"
                                className="px-4 py-3 glass rounded-lg focus:outline-none focus:border-primary-base/25"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="flex justify-between items-center">
                                <label htmlFor="password" className="text-sm font-medium text-primary-color">
                                    Password
                                </label>
                                <a href="#" className="text-sm text-primary-base hover:underline">
                                    Forgot password?
                                </a>
                            </div>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Enter your password"
                                className="px-4 py-3 glass rounded-lg focus:outline-none focus:border-primary-base/25"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Logging in...
                                </span>
                            ) : (
                                'Log In'
                            )}
                        </Button>
                    </form>

                    {/* Social Login */}
                    <div className="mt-8">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-primary-base/10"></div>
                            </div>
                            <div className="relative flex justify-center">
                                <span className="bg-white px-4 text-sm text-tertiary-color">Or continue with</span>
                            </div>
                        </div>

                        <div className="flex gap-4 mt-6">
                            <button className="flex-1 glass py-3 rounded-lg hover:bg-white/75 transition-colors flex items-center justify-center gap-2">
                                <span className="text-lg">G</span>
                                <span className="text-sm text-secondary-color">Google</span>
                            </button>
                            <button className="flex-1 glass py-3 rounded-lg hover:bg-white/75 transition-colors flex items-center justify-center gap-2">
                                <span className="text-lg">M</span>
                                <span className="text-sm text-secondary-color">Microsoft</span>
                            </button>
                        </div>
                    </div>
                </Card>

                <p className="text-center text-sm text-tertiary-color mt-6">
                    Don't have an account?{' '}
                    <Link to="/signup" className="text-primary-base hover:underline">Sign up for free</Link>
                </p>
            </div>
        </Section>
    )
}
